export interface AudienceChildLike {
  participant: boolean | null;
  metadata: { value: { completes: number | null } | null };
}

export interface InsightsChildLike {
  feasibility: { total: number; cpi: number };
}

export interface ParticipantLike {
  participant: boolean | null;
}

/**
 * `participant` lives in the core registry, not the audience child state
 * (see `SurveyChildRegistryState`/`SurveyCreationChildAudienceState`); this
 * merges it back in by key so the stats helpers can stay agnostic of state
 * layout.
 */
export function mergeParticipant<TChild extends object>(
  audienceChildren: Record<string, TChild>,
  coreChildren: Record<string, ParticipantLike>,
): Record<string, TChild & ParticipantLike> {
  return Object.fromEntries(
    Object.entries(audienceChildren).map(([key, child]) => [
      key,
      { ...child, participant: coreChildren[key]?.participant ?? null },
    ]),
  );
}

function selectedChildEntries<TChild extends { participant: boolean | null }>(
  audienceChildren: Record<string, TChild>,
): [string, TChild][] {
  return Object.entries(audienceChildren).filter(
    ([, child]) => child.participant,
  );
}

export function sumChildrenFeasibility(
  audienceChildren: Record<string, AudienceChildLike>,
  insightsChildren: Record<string, InsightsChildLike>,
): number {
  return selectedChildEntries(audienceChildren).reduce(
    (sum, [key]) => sum + (insightsChildren[key]?.feasibility.total ?? 0),
    0,
  );
}

export function averageChildrenCpi(
  audienceChildren: Record<string, AudienceChildLike>,
  insightsChildren: Record<string, InsightsChildLike>,
): number {
  const selectedCpis = selectedChildEntries(audienceChildren).map(
    ([key]) => insightsChildren[key]?.feasibility.cpi ?? 0,
  );

  return selectedCpis.length
    ? selectedCpis.reduce((sum, cpi) => sum + cpi, 0) / selectedCpis.length
    : 0;
}

export function sumChildrenCompletes(
  audienceChildren: Record<string, AudienceChildLike>,
): number {
  return selectedChildEntries(audienceChildren).reduce(
    (sum, [, child]) => sum + (child.metadata.value?.completes ?? 0),
    0,
  );
}

export function sumChildrenTotalCost(
  audienceChildren: Record<string, AudienceChildLike>,
  insightsChildren: Record<string, InsightsChildLike>,
): number {
  return selectedChildEntries(audienceChildren).reduce((sum, [key, child]) => {
    const completes = child.metadata.value?.completes ?? 0;
    const cpi = insightsChildren[key]?.feasibility.cpi ?? 0;
    return sum + completes * cpi;
  }, 0);
}
